import { useDispatch, useSelector } from "react-redux";
import { requestExternalContent } from "~/features/externalContent/redux/actions";
import { useEffect, useMemo } from "react";
import { type RootState } from "~/app/store";

export const useExternalContent = (content: string | undefined | null) => {
  const dispatch = useDispatch();
  const externalContent = useSelector(selectExternalContent(content));

  useEffect(
    () => {
      if (isExternalContent(content) && !externalContent) {
        dispatch(requestExternalContent(content));
      }
    },
    // Disabled, because externalContent should not trigger refreshing this, only content
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, content],
  );

  if (!isExternalContent(content)) {
    return {
      state: "loaded",
      isExternal: false,
      content,
    };
  } else {
    return {
      state: !externalContent ? true : externalContent.state,
      isExternal: true,
      content: !externalContent ? undefined : externalContent.data,
    };
  }
};

export const useMultipleExternalContent = (...contentList: (string | undefined)[]) => {
  const dispatch = useDispatch();
  const externalContentMap = useSelector(selectExternalContentMap);

  useEffect(
    () => {
      contentList.forEach((content) => {
        if (!content) {
          return;
        }
        const externalContent = externalContentMap[content];
        if (isExternalContent(content) && !externalContent) {
          dispatch(requestExternalContent(content));
        }
      });
    },
    // Spread prevents from statically checking exhaustive-deps, so we do it at our own risk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, ...contentList],
  );

  return useMemo(
    () => {
      return contentList.map((content) => {
        if (!isExternalContent(content)) {
          return {
            state: "loaded",
            isExternal: false,
            content,
          };
        } else {
          const externalContent = externalContentMap[content];
          return {
            state: !externalContent ? true : externalContent.state,
            isExternal: true,
            content: !externalContent ? undefined : externalContent.data,
          };
        }
      });
    },
    // Spread prevents from statically checking exhaustive-deps, so we do it at our own risk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, externalContentMap, ...contentList],
  );
};

const selectExternalContentMap = (state: RootState) => state.externalContent;

export function isExternalContent(info?: string | null): info is string {
  return !!info && typeof info === "string" && info.startsWith("http");
}

const selectExternalContent = (info?: string | null) => (state: RootState) =>
  isExternalContent(info) ? state.externalContent[info] : undefined;
